import { useState } from 'react';
import { useMutation } from 'react-query';
import { HStack, IconButton, Spinner, VStack, useTheme } from 'native-base';
import { useTranslation } from 'react-i18next';

import { DeviceMode } from '@pimm/services/lib/store-equipment';
import { SetDeviceMode } from '@pimm/services/lib/store-equipment/services';
import { Button, Modal, Text } from '@pimm/base';
import { Tool01Icon } from '../icons/tool-01';

type DataSyncButtonProps = {
  siteId?: string;
  canDataSync?: boolean;
  onSynced?: () => void;
};

export const DataSyncButton = ({ siteId, canDataSync, onSynced }: DataSyncButtonProps) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const syncData = useMutation(
    // @ts-expect-error type mismatch is in the API definition, not in how the argument is passed
    (id: string) => SetDeviceMode(id, { Mode: DeviceMode.Locked60 }),
    {
      onSuccess: () => {
        setIsOpen(false);
        onSynced?.();
      },
    },
  );

  const handleConfirm = () => {
    if (siteId) syncData.mutate(siteId);
  };

  if (!canDataSync) return null;

  return (
    <>
      <IconButton
        rounded="lg"
        p={0}
        boxSize={10}
        disabled={syncData.isLoading}
        borderWidth={1}
        borderColor="gray.300"
        _hover={{ borderColor: 'primary.600', _icon: { color: colors.primary[600] } }}
        _pressed={{ borderColor: 'primary.600', _icon: { color: colors.primary[600] } }}
        _focus={{ borderColor: 'gray.300' }}
        icon={syncData.isLoading ? <Spinner size="sm" variant="primary" /> : <Tool01Icon size="md" color={colors.gray[700]} />}
        onPress={() => setIsOpen(true)}
      />
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} _content={{ rounded: 'xl', p: 0, w: 400 }}>
        <VStack space={0}>
          <VStack px={5} pt={5} pb={2} space={0}>
            <Text size="xl" fontWeight={700} color="gray.900" pr={8}>
              {t('store_equipment:data_sync')}
            </Text>
          </VStack>
          <VStack px={5} pb={4} space={3}>
            <Text size="lg" color="gray.800">
              {t('store_equipment:data_sync_confirmation_message')}
            </Text>
            {syncData.isError && (
              <Text size="sm" color="error.600" fontWeight={500}>
                {t('store_equipment:sorry_message')}
              </Text>
            )}
          </VStack>
          <HStack space={2} px={5} pb={5} justifyContent="flex-end">
            <Button alternate outline bg="white" minH={9} disabled={syncData.isLoading} onPress={() => setIsOpen(false)}>
              {t('store_equipment:cancel')}
            </Button>
            <Button isLoading={syncData.isLoading} minH={9} onPress={handleConfirm}>
              {t('store_equipment:confirm')}
            </Button>
          </HStack>
        </VStack>
      </Modal>
    </>
  );
};
